function has_failed_response(parsed_response) {
    const did_fail = parsed_response?.status && parsed_response.status !== "success";
    return did_fail;
}

function normalize_candidate_url(value) {
    const normalized_candidate = String(value || "").trim();
    return normalized_candidate;
}

function resolve_url(value) {
    try {
        return new URL(String(value || "").trim());
    } catch {
        return null;
    }
}

function get_submitters(parsed_response) {
    return Array.isArray(parsed_response?.data?.submitters)
        ? parsed_response.data.submitters
        : [];
}

function get_candidate_urls(record = {}, keys = []) {
    return keys
    .map((key) => normalize_candidate_url(Reflect.get(record ?? {}, key)))
    .filter(Boolean);
}

function extract_inline_preview_url(parsed_response) {
    const data = parsed_response?.data || {};
    const submitters = get_submitters(parsed_response);
    const submission_preview_keys = [
        "presigned_url",
        "preview_url",
        "document_url",
        "file_url",
        "download_url",
        "pdf_url"
    ];
    const fallback_keys = ["url"];

    const submitter_candidate = submitters
        .flatMap((submitter) => get_candidate_urls(submitter, submission_preview_keys))
        .find(Boolean);
    if (submitter_candidate) {
        return submitter_candidate;
    }

    const top_level_candidates = get_candidate_urls(data, submission_preview_keys);
    if (top_level_candidates.length) {
        return top_level_candidates[0];
    }

    const submitterFallbackCandidate = submitters
        .flatMap((submitter) => get_candidate_urls(submitter, fallback_keys))
        .find(Boolean);
    if (submitterFallbackCandidate) {
        return submitterFallbackCandidate;
    }

    const topLevelFallbackCandidate = get_candidate_urls(data, fallback_keys).find(Boolean);
    return topLevelFallbackCandidate || "";
}

function extract_external_preview_url(parsed_response) {
    const submitters = get_submitters(parsed_response);
    const external_url = submitters
        .flatMap((submitter) => get_candidate_urls(submitter, ["embed_src", "link", "url"]))
        .find(Boolean);
    return external_url || "";
}

function extract_submitter_slug(parsed_response) {
    const submitters = get_submitters(parsed_response);
    const slug = submitters
        .flatMap((submitter) => get_candidate_urls(submitter, ["slug", "submitter_slug"]))
        .find(Boolean);
    return slug || "";
}

function extract_submitter_token(parsed_response, external_preview_url = "") {
    const submitters = get_submitters(parsed_response);
    const token = submitters
        .flatMap((submitter) => get_candidate_urls(submitter, ["token", "jwt", "jwt_token", "access_token", "submitter_token"]))
        .find(Boolean);
    if (token) {
        return token;
    }

    const resolved_external_url = resolve_url(external_preview_url);
    const query_token = resolved_external_url?.searchParams?.get("token") || "";
    return normalize_candidate_url(query_token);
}

function extract_docuseal_form_src(parsed_response, external_preview_url = "") {
    const submitter_slug = extract_submitter_slug(parsed_response);
    const resolved_external_url = resolve_url(external_preview_url);

    if (submitter_slug) {
        const docuseal_origin = resolved_external_url?.origin || "https://docuseal.com";
        return `${docuseal_origin}/s/${submitter_slug}`;
    }

    if (resolved_external_url && (resolved_external_url.pathname.startsWith("/s/") || resolved_external_url.pathname.startsWith("/d/"))) {
        return `${resolved_external_url.origin}${resolved_external_url.pathname}`;
    }

    if (resolved_external_url) {
        return `${resolved_external_url.origin}${resolved_external_url.pathname}${resolved_external_url.search}`;
    }

    return "";
}

function is_preview_error_result(result) {
    const is_preview_error = Object.hasOwn(result || {}, "populated_preview_url");
    return is_preview_error;
}

export function build_preview_error_result(message) {
    const error_result = {
        populated_preview_url: "",
        populated_preview_external_url: "",
        populated_preview_form_src: "",
        populated_preview_token: "",
        populated_preview_error: message
    };
    return error_result;
}

function build_populated_preview_success(preview_url, external_preview_url, form_src, token) {
    const preview_success_result = {
        populated_preview_url: preview_url,
        populated_preview_external_url: external_preview_url,
        populated_preview_form_src: form_src,
        populated_preview_token: token,
        populated_preview_error: ""
    };
    return preview_success_result;
}

export function get_missing_preview_context_error(application_id, template_entity_type_id) {
    if (application_id && template_entity_type_id) {
        const empty_error_message = "";
        return empty_error_message;
    }
    const error_message = "The populated document preview needs a template and application context.";
    return error_message;
}

function get_preview_url_error(preview_url, external_preview_url) {
    if (preview_url || external_preview_url) {
        const empty_error_message = "";
        return empty_error_message;
    }
    const error_message = "The populated document preview did not return a usable document URL.";
    return error_message;
}

function get_preview_error_result(preview_result) {
    const preview_error_result = is_preview_error_result(preview_result)
        ? preview_result
        : null;
    return preview_error_result;
}

function get_preview_validation_result(vm, preview_result) {
    const preview_error_result = get_preview_error_result(preview_result);
    if (preview_error_result) {
        const resolved_preview_error_result = preview_error_result;
        return resolved_preview_error_result;
    }

    if (!has_failed_response(preview_result)) {
        const no_validation_error = null;
        return no_validation_error;
    }
    const error_message = preview_result?.message || "Failed to create the populated document preview.";
    vm.show_error(error_message);
    const preview_failure_result = build_preview_error_result(error_message);
    return preview_failure_result;
}

export function get_missing_context_result(application_id, template_entity_type_id) {
    const missing_context_error = get_missing_preview_context_error(
        application_id,
        template_entity_type_id
    );
    const missing_context_result = missing_context_error
        ? build_preview_error_result(missing_context_error)
        : null;
    return missing_context_result;
}

function get_preview_url_error_result(vm, preview_url, external_preview_url) {
    const preview_url_error = get_preview_url_error(preview_url, external_preview_url);
    if (!preview_url_error) {
        const no_preview_url_error = null;
        return no_preview_url_error;
    }
    vm.show_error(preview_url_error);
    const preview_url_error_result = build_preview_error_result(preview_url_error);
    return preview_url_error_result;
}

export function resolve_preview_result(vm, preview_result) {
    const preview_validation_result = get_preview_validation_result(vm, preview_result);
    if (preview_validation_result) {
        return preview_validation_result;
    }

    const preview_url = extract_inline_preview_url(preview_result);
    const external_preview_url = extract_external_preview_url(preview_result);
    const populated_preview_form_src = extract_docuseal_form_src(preview_result, external_preview_url);
    const populated_preview_token = extract_submitter_token(preview_result, external_preview_url);
    const preview_url_error_result = get_preview_url_error_result(vm, preview_url, external_preview_url);
    if (preview_url_error_result) {
        return preview_url_error_result;
    }

    const populated_preview_success = build_populated_preview_success(
        preview_url,
        external_preview_url || preview_url,
        populated_preview_form_src,
        populated_preview_token
    );
    return populated_preview_success;
}
